/* Customer tracking-link QR — click to open a printable dialog for a customer.
   Uses qrcode.react (already a dependency for delivery-docket QRs). */
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Copy, Printer } from "lucide-react";
import { toast } from "sonner";
import { printQrPage } from "@/lib/printQr";

export default function CustomerTrackQR({ open, onOpenChange, customer }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !customer) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.post(`/customers/${customer.id}/ensure-track-token`);
        setUrl(`${window.location.origin}/track/${r.data.track_token}`);
      } catch {
        toast.error("Could not fetch tracking link");
      } finally { setLoading(false); }
    })();
  }, [open, customer]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  const downloadPng = () => {
    const svg = document.getElementById("track-qr-svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 800; canvas.height = 800;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 800, 800);
      ctx.drawImage(img, 40, 40, 720, 720);
      canvas.toBlob((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `gadtech-tracker-${(customer?.name || "customer").replace(/\s+/g, "_")}.png`;
        a.click();
        URL.revokeObjectURL(a.href);
        URL.revokeObjectURL(svgUrl);
      }, "image/png");
    };
    img.src = svgUrl;
  };

  const printPage = () => {
    printQrPage({
      title: `Tracking QR · ${customer?.name || ""}`,
      tagLine: "ARMORBATCH GLOBAL · Live Delivery Tracker",
      heading: customer?.name || "",
      description: "Scan the QR code below with your phone camera to open a live tracker for your deliveries. Bookmark the page — it works every day.",
      footer: "© ARMORBATCH GLOBAL",
      url,
      svgElementId: "track-qr-svg",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-industrial max-w-md" data-testid="track-qr-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase">Tracking QR · <span className="text-primary-orange">{customer?.name}</span></DialogTitle>
        </DialogHeader>

        {loading && <div className="label-tag text-primary-orange pulse-orange py-4">Generating…</div>}

        {url && !loading && (
          <>
            <div className="flex justify-center py-4">
              <div className="p-4 bg-white rounded-xl border-2 border-primary-orange">
                <QRCodeSVG id="track-qr-svg" value={url} size={220} level="M" includeMargin={false} />
              </div>
            </div>
            <div className="mono text-[10px] text-muted-industrial break-all border border-industrial rounded-md px-2 py-1.5 text-center">
              {url}
            </div>
            <div className="flex gap-2 mt-3">
              <Button data-testid="qr-copy" onClick={copyUrl} variant="outline" className="rounded-lg border-industrial flex-1"><Copy className="w-4 h-4 mr-2" /> Copy URL</Button>
              <Button data-testid="qr-download" onClick={downloadPng} variant="outline" className="rounded-lg border-industrial flex-1"><Download className="w-4 h-4 mr-2" /> PNG</Button>
              <Button data-testid="qr-print" onClick={printPage} className="btn-cta flex-1"><Printer className="w-4 h-4 mr-2" /> Print</Button>
            </div>
            <p className="text-[11px] text-muted-industrial mt-3 text-center">
              Print &amp; stick on the delivery docket · attach to invoice emails · save to a business card.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
